import { useState } from "react";
import { Mail, Phone, MessageCircle, ChevronDown } from "lucide-react";
import { TikTokIcon } from "@/components/TikTokIcon";
import { toast } from "sonner";
import { WHATSAPP_NUMBER, WHATSAPP_DISPLAY, TIKTOK_URL, TIKTOK_HANDLE, buildWhatsAppOrderUrl } from "@/lib/contact";

const faqs = [
  {
    q: "Berapa lama tempoh penghantaran?",
    a: "Setiap sangkar dibuat secara custom mengikut tempahan. Tempoh siap pembinaan biasanya 14-30 hari bekerja, kemudian penghantaran mengambil masa 2-4 hari bekerja untuk Semenanjung Malaysia, dan 5-7 hari untuk Sabah & Sarawak.",
  },
  {
    q: "Bolehkah saya tempah saiz khas?",
    a: "Sudah tentu! Semua sangkar kami dibuat custom mengikut saiz pilihan anda. WhatsApp kami untuk berbincang spesifikasi dan dapatkan sebut harga percuma.",
  },
  {
    q: "Bagaimana cara untuk membuat tempahan?",
    a: "Mudah sahaja! Pilih sangkar yang anda minat dari katalog, klik 'Tempah via WhatsApp', dan kami akan bantu anda dengan sebut harga, saiz, dan pembayaran.",
  },
  {
    q: "Kaedah pembayaran apa yang diterima?",
    a: "Kami menerima pindahan bank (Maybank, CIMB, Public Bank). Deposit 50% diperlukan untuk memulakan pembinaan, baki dibayar sebelum penghantaran.",
  },
  {
    q: "Bolehkah saya beli secara borong?",
    a: "Sudah tentu! Kami menawarkan harga khas untuk pet shop, breeder, dan pet hotel. Sila WhatsApp kami untuk perbincangan lanjut.",
  },
  {
    q: "Adakah produk anda mempunyai jaminan?",
    a: "Ya, semua sangkar kami datang dengan jaminan 1 tahun terhadap kerosakan pembinaan.",
  },
];

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [open, setOpen] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    toast.success("Terima kasih! Kami akan hubungi anda tidak lama lagi.");
    setForm({ name: "", email: "", message: "" });
  };

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-14 lg:py-20" data-testid="contact-page">
      <div className="max-w-2xl">
        <div className="label-eyebrow text-[#E07A5F]">Hubungi & FAQ</div>
        <h1 className="font-heading font-bold text-5xl sm:text-6xl tracking-tight text-[#1C1C1C] mt-3 text-balance">
          Mari berbual.
        </h1>
        <p className="text-base text-[#4A4A4A] mt-5 leading-relaxed">
          Ada pertanyaan tentang produk atau tempahan? WhatsApp kami terus untuk respons paling cepat.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mt-14">
        <div className="lg:col-span-5">
          {/* WhatsApp big CTA */}
          <a
            href={buildWhatsAppOrderUrl()}
            target="_blank"
            rel="noreferrer"
            data-testid="contact-whatsapp-cta"
            className="block bg-[#25D366] hover:bg-[#1EBE57] text-white rounded-2xl p-6 transition-all duration-300 shadow-[0_10px_30px_rgba(37,211,102,0.25)] hover:shadow-[0_14px_40px_rgba(37,211,102,0.35)]"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-white/15 flex items-center justify-center">
                <MessageCircle className="w-6 h-6" />
              </div>
              <div>
                <div className="font-heading font-semibold text-lg">WhatsApp Kami</div>
                <div className="text-sm text-white/90">{WHATSAPP_DISPLAY}</div>
              </div>
            </div>
            <p className="text-sm text-white/85 mt-4 leading-relaxed">
              Klik untuk mula berbual. Sebut harga dan konsultasi saiz percuma.
            </p>
          </a>

          <div className="space-y-5 mt-6">
            <ContactRow icon={Phone} label="Telefon" value={WHATSAPP_DISPLAY} href={`tel:+${WHATSAPP_NUMBER}`} testid="contact-phone" />
            <ContactRow icon={TikTokIcon} label="TikTok" value={`@${TIKTOK_HANDLE}`} href={TIKTOK_URL} testid="contact-tiktok" />
            <ContactRow icon={Mail} label="Emel" value="hello@sangkarmurah.my" href="mailto:hello@sangkarmurah.my" testid="contact-email" />
          </div>

          <form onSubmit={handleSubmit} className="mt-8 bg-white rounded-2xl p-6 sm:p-8 border border-[rgba(28,28,28,0.08)]">
            <h3 className="font-heading font-semibold text-xl text-[#1C1C1C]">Hantar mesej</h3>
            <div className="space-y-4 mt-5">
              <input
                required
                placeholder="Nama anda"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="contact-name-input"
                className="w-full px-4 py-3 bg-[#FAF9F6] border border-[rgba(28,28,28,0.12)] rounded-xl text-sm focus:outline-none focus:border-[#E07A5F]"
              />
              <input
                type="email"
                required
                placeholder="Emel anda"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="contact-email-input"
                className="w-full px-4 py-3 bg-[#FAF9F6] border border-[rgba(28,28,28,0.12)] rounded-xl text-sm focus:outline-none focus:border-[#E07A5F]"
              />
              <textarea
                required
                rows={4}
                placeholder="Mesej anda..."
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                data-testid="contact-message-input"
                className="w-full px-4 py-3 bg-[#FAF9F6] border border-[rgba(28,28,28,0.12)] rounded-xl text-sm focus:outline-none focus:border-[#E07A5F] resize-none"
              />
              <button
                type="submit"
                data-testid="contact-submit-btn"
                className="w-full px-6 py-3.5 rounded-xl bg-[#1C1C1C] text-[#FAF9F6] hover:bg-[#E07A5F] transition-colors font-semibold text-sm"
              >
                Hantar Mesej
              </button>
            </div>
          </form>
        </div>

        <div className="lg:col-span-7">
          <div className="label-eyebrow text-[#E07A5F]">Soalan Lazim</div>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-[#1C1C1C] mt-3">
            Soalan yang sering ditanya.
          </h2>

          <div className="mt-8 space-y-3">
            {faqs.map((f, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl border border-[rgba(28,28,28,0.08)] overflow-hidden"
                data-testid={`faq-item-${i}`}
              >
                <button
                  onClick={() => setOpen(open === i ? null : i)}
                  className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
                  data-testid={`faq-toggle-${i}`}
                >
                  <span className="font-heading font-semibold text-base text-[#1C1C1C]">{f.q}</span>
                  <ChevronDown className={`w-4 h-4 text-[#4A4A4A] transition-transform shrink-0 ${open === i ? "rotate-180" : ""}`} />
                </button>
                {open === i && (
                  <div className="px-6 pb-6 text-sm text-[#4A4A4A] leading-relaxed">{f.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const ContactRow = ({ icon: Icon, label, value, href, testid }) => {
  const inner = (
    <div className="flex items-start gap-4 group">
      <div className="w-11 h-11 rounded-xl bg-[#E07A5F]/10 text-[#E07A5F] flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <div className="label-eyebrow text-[#4A4A4A]">{label}</div>
        <div className="text-sm font-semibold text-[#1C1C1C] mt-1 group-hover:text-[#E07A5F]">{value}</div>
      </div>
    </div>
  );
  return href ? (
    <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" data-testid={testid}>
      {inner}
    </a>
  ) : (
    <div data-testid={testid}>{inner}</div>
  );
};

export default Contact;
